import React, { useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable, DataTableExpandedRows } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { TagProps } from "primereact/tag";

import * as client from "../../../client";
import { useToast } from "../../../context-providers/Toast";
import { apiErrorDetail } from "../../../api/errors";
import { usePolicyApplications } from "../policy-application/data";
import { formatCurrency } from "../policy-application/util";
import {
  useDeleteEndorsement,
  useEndorsements,
  useRegenerateEndorsement,
  useUpdateEndorsementStatus,
} from "./data";
import { EndorsementPremiumDialog } from "./EndorsementPremiumDialog";
import { EndorsementSovDiffPanel } from "./EndorsementSovDiffPanel";
import { NewEndorsementDialog } from "./NewEndorsementDialog";
import styles from "./Endorsements.module.css";

interface EndorsementsTabProps {
  specialtyPropertyInfo: client.ReportResponse;
}

const STATUS_LABELS: { [status in client.EndorsementStatus]: string } = {
  draft: "Draft",
  generating: "Generating",
  needs_review: "Needs Review",
  verified: "Verified",
  voided: "Voided",
};

const STATUS_SEVERITIES: {
  [status in client.EndorsementStatus]: TagProps["severity"];
} = {
  draft: null,
  generating: "info",
  needs_review: "warning",
  verified: "success",
  voided: "danger",
};

const formatTiv = (value: number | null): string =>
  value === null ? "—" : formatCurrency(value);

const DeltaCell: React.FC<{ value: number | null }> = ({ value }) => {
  if (value === null) {
    return <span>—</span>;
  }
  const className =
    value < 0 ? styles.negativeDelta : value > 0 ? styles.positiveDelta : "";
  return (
    <span className={className}>
      {value < 0 ? "−" : "+"}
      {formatCurrency(Math.abs(value))}
    </span>
  );
};

// A premium amount that opens the editor while the entry is still amendable.
const PremiumCell: React.FC<{
  row: client.EndorsementView;
  value: number | null;
  onEdit: (row: client.EndorsementView) => void;
}> = ({ row, value, onEdit }) => {
  const editable = row.status !== "verified" && row.status !== "voided";
  if (!editable) {
    return <DeltaCell value={value} />;
  }
  return (
    <button
      className={styles.premiumCell}
      title="Click to amend the premium change"
      onClick={() => onEdit(row)}
    >
      <DeltaCell value={value} />
    </button>
  );
};

// Whether the generated schedule tied to the entered target, to the dollar —
// the generation task's own reconciliation standard.
const TivTieIcon: React.FC<{ row: client.EndorsementView }> = ({ row }) => {
  if (row.tiv_target === null || row.tiv_achieved === null) {
    return null;
  }
  const delta = row.tiv_achieved - row.tiv_target;
  if (Math.abs(delta) < 1) {
    return (
      <i
        className={`pi pi-check-circle ${styles.tieOk}`}
        title="Generated schedule ties to the TIV target"
      />
    );
  }
  return (
    <i
      className={`pi pi-times-circle ${styles.tieMiss}`}
      title={`Off target by ${formatCurrency(delta)}`}
    />
  );
};

export const EndorsementsTab: React.FC<EndorsementsTabProps> = ({
  specialtyPropertyInfo,
}) => {
  const reportId = specialtyPropertyInfo.report_id;
  const toast = useToast();
  const { data, isLoading } = useEndorsements(reportId);
  const { data: applications } = usePolicyApplications(reportId);
  const updateStatus = useUpdateEndorsementStatus(reportId);
  const deleteEndorsement = useDeleteEndorsement(reportId);
  const regenerate = useRegenerateEndorsement(reportId);

  const [createVisible, setCreateVisible] = useState(false);
  const [premiumFor, setPremiumFor] = useState<client.EndorsementView | null>(
    null
  );
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | client.EndorsementView[]
  >([]);

  const layersById = new Map<string, client.Layer>();
  for (const layer of specialtyPropertyInfo.report_json.layer_info) {
    if (layer.layer_id) {
      layersById.set(layer.layer_id, layer);
    }
  }
  const applicationsById = new Map(
    (applications ?? []).map((app) => [app.id, app])
  );

  const handleStatusChange = (
    endorsement: client.EndorsementView,
    status: client.EndorsementStatus
  ) => {
    updateStatus.mutate(
      { endorsementId: endorsement.id, status },
      {
        onError: (err) => {
          toast.show({
            severity: "error",
            summary: `Could not mark the endorsement ${STATUS_LABELS[status].toLowerCase()}`,
            detail: apiErrorDetail(err) ?? "Please try again.",
            life: 5000,
          });
        },
      }
    );
  };

  const confirmDelete = (endorsement: client.EndorsementView) => {
    confirmDialog({
      message: `Delete draft endorsement #${endorsement.endorsement_number}? Its documents are removed from the report.`,
      header: "Delete draft",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      accept: () => deleteEndorsement.mutate(endorsement.id),
    });
  };

  if (isLoading) {
    return (
      <div className={styles.empty}>
        <ProgressSpinner style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  const endorsements = data?.endorsements ?? [];
  const totalsByPolicy = new Map(
    (data?.totals ?? []).map((t) => [t.policy_application_id, t])
  );
  const policyIds = Array.from(
    new Set(endorsements.map((e) => e.policy_application_id))
  );

  const policyTitle = (policyApplicationId: string): string => {
    const app = applicationsById.get(policyApplicationId);
    if (!app) {
      return "Unknown policy";
    }
    return `Policy ${app.suffix}${
      app.policy_number ? ` · ${app.policy_number}` : ""
    }`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.title}>Endorsements</span>
        <Button
          label="New endorsement"
          icon="pi pi-plus"
          size="small"
          onClick={() => setCreateVisible(true)}
          disabled={(applications ?? []).length === 0}
          tooltip={
            (applications ?? []).length === 0
              ? "Create policies from layers first (Policies tab)."
              : undefined
          }
          tooltipOptions={{ position: "left", showOnDisabled: true }}
        />
      </div>

      {policyIds.length === 0 && (
        <div className={styles.empty}>
          <i className={`pi pi-file-edit ${styles.emptyIcon}`} />
          <span>No endorsements on this report yet.</span>
        </div>
      )}

      {policyIds.map((policyApplicationId) => {
        const rows = endorsements.filter(
          (e) => e.policy_application_id === policyApplicationId
        );
        const totals = totalsByPolicy.get(policyApplicationId);
        return (
          <div key={policyApplicationId} className={styles.policySection}>
            <div className={styles.policyHeader}>
              <span className={styles.policyTitle}>
                {policyTitle(policyApplicationId)}
              </span>
              {totals && (
                <span className={styles.totals}>
                  {totals.base_premium !== null && (
                    <span>
                      Base (our share){" "}
                      <b>{formatCurrency(totals.base_premium)}</b>
                    </span>
                  )}
                  <span>
                    Owed for term <b>{formatCurrency(totals.owed_premium)}</b>
                  </span>
                  <span>
                    Full term <b>{formatCurrency(totals.term_premium)}</b>
                  </span>
                </span>
              )}
            </div>
            <DataTable
              value={rows}
              size="small"
              dataKey="id"
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              rowExpansionTemplate={(row: client.EndorsementView) => (
                <EndorsementSovDiffPanel
                  endorsementId={row.id}
                  summary={row.generation_summary}
                />
              )}
            >
              <Column
                expander={(row: client.EndorsementView) =>
                  row.has_generated_sov
                }
                style={{ width: "36px" }}
              />
              <Column
                header="#"
                field="endorsement_number"
                style={{ width: "48px" }}
              />
              <Column header="Effective" field="effective_date" />
              <Column
                header="Status"
                body={(row: client.EndorsementView) =>
                  row.generation_error ? (
                    <Tag
                      value="Generation failed"
                      severity="danger"
                      rounded
                      title={row.generation_error}
                    />
                  ) : (
                    <Tag
                      value={STATUS_LABELS[row.status]}
                      severity={STATUS_SEVERITIES[row.status]}
                      rounded
                    />
                  )
                }
              />
              <Column
                header="Description"
                body={(row: client.EndorsementView) => (
                  <span className={styles.description} title={row.description}>
                    {row.description}
                  </span>
                )}
              />
              <Column
                header="TIV"
                body={(row: client.EndorsementView) =>
                  row.tiv_target === null ? (
                    "No SOV change"
                  ) : (
                    <span className={styles.tivCell}>
                      {formatTiv(row.tiv_before)} →{" "}
                      {formatTiv(row.tiv_achieved)} (target{" "}
                      {formatTiv(row.tiv_target)})
                      <TivTieIcon row={row} />
                    </span>
                  )
                }
              />
              <Column
                header="Term Δ"
                align="right"
                body={(row: client.EndorsementView) => (
                  <PremiumCell
                    row={row}
                    value={row.premium_change?.term_premium_delta ?? null}
                    onEdit={setPremiumFor}
                  />
                )}
              />
              <Column
                header="Prorated Δ"
                align="right"
                body={(row: client.EndorsementView) => (
                  <PremiumCell
                    row={row}
                    value={row.premium_change?.prorated_premium_delta ?? null}
                    onEdit={setPremiumFor}
                  />
                )}
              />
              <Column
                header="Docs"
                align="right"
                style={{ width: "60px" }}
                body={(row: client.EndorsementView) => row.document_ids.length}
              />
              <Column
                style={{ width: "150px" }}
                body={(row: client.EndorsementView) => (
                  <div className={styles.rowActions}>
                    {(row.status === "needs_review" ||
                      row.status === "generating") &&
                      row.tiv_target !== null && (
                        <Button
                          icon="pi pi-refresh"
                          text
                          rounded
                          size="small"
                          aria-label="Regenerate schedule"
                          tooltip={
                            row.status === "generating"
                              ? "Restart generation if the run died (refused while one is in progress)"
                              : "Run generation again"
                          }
                          tooltipOptions={{ position: "left" }}
                          loading={regenerate.isPending}
                          onClick={() =>
                            regenerate.mutate(row.id, {
                              onError: (err) => {
                                toast.show({
                                  severity: "error",
                                  summary: "Could not start regeneration",
                                  detail:
                                    apiErrorDetail(err) ?? "Please try again.",
                                  life: 5000,
                                });
                              },
                            })
                          }
                        />
                      )}
                    {row.status === "needs_review" && (
                      <Button
                        icon="pi pi-check"
                        text
                        rounded
                        size="small"
                        severity="success"
                        aria-label="Verify"
                        tooltip="Verify — freezes this entry into the ledger"
                        tooltipOptions={{ position: "left" }}
                        loading={updateStatus.isPending}
                        onClick={() => handleStatusChange(row, "verified")}
                      />
                    )}
                    {(row.status === "needs_review" ||
                      row.status === "verified") && (
                      <Button
                        icon="pi pi-ban"
                        text
                        rounded
                        size="small"
                        severity="danger"
                        aria-label="Void"
                        tooltip="Void — excludes this entry from totals"
                        tooltipOptions={{ position: "left" }}
                        onClick={() => handleStatusChange(row, "voided")}
                      />
                    )}
                    {row.status === "draft" && (
                      <Button
                        icon="pi pi-trash"
                        text
                        rounded
                        size="small"
                        severity="danger"
                        aria-label="Delete draft"
                        tooltip="Delete draft"
                        tooltipOptions={{ position: "left" }}
                        onClick={() => confirmDelete(row)}
                      />
                    )}
                  </div>
                )}
              />
            </DataTable>
          </div>
        );
      })}

      <NewEndorsementDialog
        reportId={reportId}
        applications={applications ?? []}
        layersById={layersById}
        currentTiv={data?.current_tiv ?? null}
        visible={createVisible}
        onHide={() => setCreateVisible(false)}
      />
      <EndorsementPremiumDialog
        reportId={reportId}
        endorsement={premiumFor}
        onHide={() => setPremiumFor(null)}
      />
    </div>
  );
};
